import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import type { FormSubmission } from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikSelect } from "./formik-controls";
import {
  type FormOrauthinfoData,
  MAX_ORAUTHINFO_MATTERS,
} from "./form-orauthinfo-types";

const GENERATE_FOR_OPTIONS = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
] as const;

const EMPTY_MATTER = { tax_years: "", tax_programs: "" };

const TIN_TYPE_OPTIONS = [
  { value: "SSN", label: "SSN" },
  { value: "ITIN", label: "ITIN" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse"]).required(),
  active_rep: Yup.string(),
  tin_type: Yup.string().oneOf(["SSN", "ITIN"]),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_years: yearRange,
        tax_programs: Yup.string().max(60, "Too long"),
      }),
    )
    .max(MAX_ORAUTHINFO_MATTERS),
});

function toInitialValues(data: Record<string, unknown>): FormOrauthinfoData {
  const d = data as Partial<FormOrauthinfoData>;
  const reps = d.representatives ?? [];
  return {
    generate_for: d.generate_for ?? "taxpayer",
    active_rep: d.active_rep ?? (reps[0]?.practitioner ?? ""),
    representatives: reps,
    matters:
      d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    tin_type: d.tin_type ?? "SSN",
    bin: d.bin ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<FormOrauthinfoData["generate_for"]>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as FormOrauthinfoData["generate_for"])}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/** Single-representative picker — "choose one representative per form". */
function RepresentativePicker() {
  const { values } = useFormikContext<FormOrauthinfoData>();
  const [field, , helpers] = useField<string>("active_rep");
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add one on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <RadioGroup value={field.value} onValueChange={(v) => helpers.setValue(v)}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Active</TableHead>
              <TableHead>Rep Name</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reps.map((rep, i) => (
              <TableRow key={rep.practitioner || i}>
                <TableCell>
                  <RadioGroupItem
                    id={`rep-${rep.practitioner}`}
                    value={rep.practitioner}
                    aria-label={`Select ${rep.name}`}
                  />
                </TableCell>
                <TableCell>
                  <Label htmlFor={`rep-${rep.practitioner}`} className="font-medium">
                    {rep.name}
                  </Label>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </RadioGroup>
    </Card>
  );
}

/** Oregon "Authorization Limitations (if any)" — tax year(s) + tax program(s). */
function LimitationsTable() {
  const { values } = useFormikContext<FormOrauthinfoData>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-56">Tax Year(s)</TableHead>
                  <TableHead>Tax Program(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Tax years for limitation ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_programs`}
                        placeholder="e.g. Personal Income Tax"
                        aria-label={`Tax programs for limitation ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove limitation ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_ORAUTHINFO_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add limitation
            </Button>
            {matters.length >= MAX_ORAUTHINFO_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Up to {MAX_ORAUTHINFO_MATTERS} limitations fit on the form.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Oregon Form OR-AUTH-INFO (Authorization for Tax Information) —
 * disclosure-only: it authorizes a *single* representative to *receive* the
 * client's confidential Oregon tax information (no representation, so no "acts
 * authorized" / exceptions section, unlike the OR-AUTH-REP power of attorney).
 */
export function FormOrauthinfoEditor({ client, submission, onSubmissionChange }: Props) {
  const isBusiness = client.type === "Business";
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>

          {isBusiness ? (
            <div className="max-w-sm">
              <Label htmlFor="bin" className="mb-1 block text-sm font-medium">
                Business Identification Number
              </Label>
              <FormikCell name="bin" aria-label="Business Identification Number" />
            </div>
          ) : (
            <div className="max-w-xs">
              <Label className="mb-1 block text-sm font-medium">
                Taxpayer's Tax ID is
              </Label>
              <FormikSelect
                name="tin_type"
                options={TIN_TYPE_OPTIONS}
                aria-label="Taxpayer tax ID type"
              />
            </div>
          )}
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Client Representative (choose one per form)</SectionHeading>
          <RepresentativePicker />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Authorization Limitations (if any)</SectionHeading>
          <LimitationsTable />
        </section>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
